import { defineComponent as _defineComponent } from 'vue'
import { onMounted, watch, computed, ref } from "vue";
  import router from "@/router";
  import { formatDate } from "@/helpers/date";
  import { useStore } from "vuex";
  import { useToast } from "vue-toastification";

  import LotsTable from "@/v2/components/LotsTable.vue";
  import CustomizeColumnsModal from "@/v2/components/CustomizeColumnsModal.vue";
  import ReleaseLotsModal from "@/components/ui/modals/ReleaseLotsModal.vue";
  import CreateOptionModal from "@/components/ui/modals/CreateOptionModal.vue";
  import SetRegistrationDateModal from "@/components/ui/modals/SetRegistrationDateModal.vue";
  import TransitionModal from "@/components/ui/modals/TransitionModal.vue";
  import EditPriceModal from "@/v2/components/ui/modals/EditPriceModal.vue";
  import plural from "@/features/plural";

  import { useLotsData } from "@/v2/composables/useLotsData";
  import type { ExtendedLot, LotListItem } from "@/v2/composables/useLotsData";
  import { useLotsSelection } from "@/v2/composables/useLotsSelection";
  import { useLotsEditing } from "@/v2/composables/useLotsEditing";
  import { useLotsColumns } from "@/v2/composables/useLotsColumns";
  import { useLotsModals } from "@/v2/composables/useLotsModals";
  import { useLotsFiltering } from "@/v2/composables/useLotsFiltering";
  import { PriceType } from "@/v2/enums/PriceType";
  import { LotStatus } from "@/enums/LotStatus";
  import LotConditionsEnum from "@/v2/enums/LotConditions";
  import { getStatusText } from "@/v2/helpers/getStatusText";
  import {
    StatusFiltersLabels,
    StatusFiltersValues,
  } from "@/constants/statusFilters";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LotsView',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();
  const toast = useToast();
  const stageId = router.currentRoute.value.params.stageId as string;

  const {
    stage,
    lots,
    lotList,
    initialHolds,
    isLoading,
    hasInitiallyLoaded,
    estateId,
    getStage,
    getEstate,
    getLots,
    getInitialHolds,
    updateUserProfile,
    initializeData,
  } = useLotsData();

  const {
    isSelectAll,
    selectedLotsList,
    selectedForReleaseList,
    selectedForOptionList,
    showCreateOption,
    selectLot,
    selectAll,
    clearSelection,
  } = useLotsSelection(lotList);

  const {
    extraColumns,
    customColumns,
    columnsArray,
    handleColumnUpdate,
    initializeColumns,
    isUpdating,
  } = useLotsColumns(stageId);

  const {
    editingLot,
    editingField,
    editingValue,
    fieldsConfig,
    columnInputTypeMap,
    getFieldType,
    getColumnValue,
    getColumnDisplayValue,
    isFieldEditable,
    getFieldOptions,
    startEditing,
    saveEdit,
    cancelEdit,
    updateEditingValue,
  } = useLotsEditing(lots, initialHolds, customColumns);

  const { filters, activeFilter, viewType, changeFilter } = useLotsFiltering();

  const refreshData = async () => {
    await getLots(stageId, activeFilter.value.value, activeFilter.value.label);
    await getInitialHolds();
  };

  const {
    showReleaseModal,
    showRegDateModal,
    showCreateOptionModal,
    showTransitionModal,
    showCustomizeColumnsModal,
    attentionType,
    clickAction,
    closeReleaseModal,
    closeCreateOptionModal,
    closeRegDateModal,
    proceed,
    onUpdateRegistrationDate,
  } = useLotsModals(
    selectedLotsList,
    selectedForOptionList,
    clearSelection,
    refreshData
  );

  const showEditPriceModal = ref(false);
  const editingLotForPrice = ref<ExtendedLot | null>(null);
  const editPriceData = ref({
    type: PriceType.FIXED,
    value: "",
  });
  const registrationDate = computed(() => {
    return stage.value?.registrationDate
      ? formatDate(stage.value.registrationDate)
      : "";
  });

  const handleFilterChange = async (item: {
    label: StatusFiltersLabels;
    value: StatusFiltersValues;
  }) => {
    changeFilter(item);
    await getLots(stageId, item.value, item.label);
    await getInitialHolds();
  };

  const handlePriceSave = async (priceData: {
    type: string;
    value: string | null;
  }) => {
    if (!editingLotForPrice.value) {
      toast.error("No lot selected for price update");
      return;
    }

    const lot = editingLotForPrice.value;

    const priceTracking = {
      type: priceData.type,
      value:
        priceData.type === PriceType.HIDE || priceData.type === PriceType.OFFERS
          ? null
          : priceData.value
          ? parseFloat(priceData.value.replace(/,/g, ""))
          : null,
    };

    try {
      await store.dispatch("lot/updateLotV2", {
        id: lot._id,
        data: {
          priceTracking: priceTracking,
        },
      });

      lot.priceTracking = {
        type: priceTracking.type,
        value: priceTracking.value?.toString() || null,
      };

      toast.success("Price updated successfully");
    } catch (error) {
      console.error("Error updating lot price:", error);
      toast.error("Failed to update price. Please try again.");
    } finally {
      editingLotForPrice.value = null;
    }
  };

  const openEditPriceModal = (lot: ExtendedLot) => {
    editingLotForPrice.value = lot;

    const priceTracking = lot.priceTracking;
    let pricingType = PriceType.FIXED;
    let amount = "";

    if (priceTracking) {
      pricingType = (priceTracking.type as PriceType) || PriceType.FIXED;
      amount = priceTracking.value?.toString() || "";
    }

    editPriceData.value = {
      type: pricingType,
      value: amount,
    };
    showEditPriceModal.value = true;
  };

  const bulkStatusUpdate = async (data: {
    selectedLots: LotListItem[];
    newStatus: string;
  }) => {
    try {
      const lotIds = data.selectedLots.map((lot) => lot.id);

      await store.dispatch("lot/bulkUpdateLots", {
        lotIds,
        status: data.newStatus,
      });

      lots.value.forEach((lot) => {
        if (lotIds.includes(lot._id)) {
          lot.status = data.newStatus as LotStatus;

          if (data.newStatus === LotStatus.UNCONDITIONAL) {
            lot.conditions = LotConditionsEnum.UNCONDITIONAL;
          }
        }
      });

      clearSelection();

      toast.success(
        `Successfully updated ${lotIds.length} ${
          lotIds.length === 1 ? "lot" : "lots"
        } to ${data.newStatus}`
      );

      await refreshData();
    } catch (error) {
      console.error("Error updating lots:", error);
      toast.error("Failed to update lots. Please try again.");
    }
  };

  const handleCancelHold = async (lot: ExtendedLot) => {
    try {
      const previousBuyers = lot.lotBuyers || null;

      await store.dispatch("lot/cancelHold", {
        lotId: lot._id,
        previousBuyers: previousBuyers,
      });

      lot.status = LotStatus.AVAILABLE;
      lot.lotBuyers = undefined;

      clearSelection();
      toast.success(`Successfully cancelled hold on Lot ${lot.title}`);

      await refreshData();
    } catch (error) {
      console.error("Error cancelling hold:", error);
      toast.error("Failed to cancel hold. Please try again.");
    }
  };

  const handleBulkCancelHold = async (data: {
    holdLots: LotListItem[];
    nonHoldLots: LotListItem[];
  }) => {
    try {
      if (data.holdLots.length > 0) {
        await Promise.all(
          data.holdLots.map((lotItem) => {
            const lot = lots.value.find((l) => l._id === lotItem.id);
            const previousBuyers = lot?.lotBuyers || null;

            return store.dispatch("lot/cancelHold", {
              lotId: lotItem.id,
              previousBuyers: previousBuyers,
            });
          })
        );

        lots.value.forEach((lot) => {
          if (data.holdLots.some((l) => l.id === lot._id)) {
            lot.status = LotStatus.AVAILABLE;
            lot.lotBuyers = undefined;
          }
        });
      }

      if (data.nonHoldLots.length > 0) {
        const nonHoldLotIds = data.nonHoldLots.map((lot) => lot.id);

        await store.dispatch("lot/bulkUpdateLots", {
          lotIds: nonHoldLotIds,
          status: LotStatus.AVAILABLE,
        });

        lots.value.forEach((lot) => {
          if (nonHoldLotIds.includes(lot._id)) {
            lot.status = LotStatus.AVAILABLE;
            lot.lotBuyers = undefined;
          }
        });
      }

      clearSelection();

      const totalUpdated = data.holdLots.length + data.nonHoldLots.length;

      toast.success(
        `Successfully updated ${totalUpdated} ${
          totalUpdated === 1 ? "lot" : "lots"
        } to Available`
      );

      await refreshData();
    } catch (error) {
      console.error("Error cancelling holds:", error);
      toast.error("Failed to cancel holds. Please try again.");
    }
  };

  const handleSetHold = async (data: {
    lot: ExtendedLot;
    buyers: { firstBuyer?: string; secondBuyer?: string };
    status?: string;
  }) => {
    const targetStatus = data.status || LotStatus.HOLD;

    try {
      if (targetStatus === LotStatus.UNDER_CONTRACT) {
        await store.dispatch("lot/setUnderContract", {
          lotId: data.lot._id,
          buyers: data.buyers,
        });
      } else if (targetStatus === LotStatus.UNCONDITIONAL) {
        await store.dispatch("lot/setUnconditional", {
          lotId: data.lot._id,
          buyers: data.buyers,
        });
      } else {
        await store.dispatch("lot/setHold", {
          lotId: data.lot._id,
          buyers: data.buyers,
        });
      }

      data.lot.status = targetStatus as LotStatus;
      data.lot.lotBuyers = data.buyers;

      clearSelection();

      const statusText = getStatusText(targetStatus, { form: "action" });

      toast.success(`Successfully put Lot ${data.lot.title} ${statusText}`);

      await refreshData();
    } catch (error) {
      console.error(`Error setting ${targetStatus}:`, error);
      toast.error(`Failed to update lot status. Please try again.`);
    }
  };

  const handleBulkSetHold = async (data: {
    lots: LotListItem[];
    buyers: { firstBuyer?: string; secondBuyer?: string };
    status?: string;
  }) => {
    const targetStatus = data.status || LotStatus.HOLD;

    try {
      const lotIds = data.lots.map((lot) => lot.id);

      await store.dispatch("lot/bulkUpdateLots", {
        lotIds,
        status: targetStatus,
        buyers: data.buyers,
      });

      lots.value.forEach((lot) => {
        if (lotIds.includes(lot._id)) {
          lot.status = targetStatus as LotStatus;
          lot.lotBuyers = data.buyers;
        }
      });

      clearSelection();

      const statusText = getStatusText(targetStatus, { form: "action" });

      toast.success(
        `Successfully put ${data.lots.length} ${
          data.lots.length === 1 ? "lot" : "lots"
        } ${statusText}`
      );

      await refreshData();
    } catch (error) {
      console.error(`Error setting ${targetStatus}:`, error);
      toast.error(`Failed to update lots status. Please try again.`);
    }
  };

  const handleBulkSetHoldWithBuyers = async (data: {
    lots: LotListItem[];
    buyersData: Array<{ firstBuyer: string; secondBuyer: string }>;
    status?: string;
  }) => {
    const targetStatus = data.status || LotStatus.HOLD;

    try {
      const updates = data.lots.map((lot, index) => ({
        lotId: lot.id,
        status: targetStatus,
        buyers: data.buyersData[index],
      }));

      await store.dispatch("lot/bulkUpdateLotsWithBuyers", {
        updates,
      });

      lots.value.forEach((lot) => {
        const index = data.lots.findIndex((l) => l.id === lot._id);

        if (index !== -1) {
          lot.status = targetStatus as LotStatus;
          lot.lotBuyers = data.buyersData[index];
        }
      });

      clearSelection();

      const statusText = getStatusText(targetStatus, { form: "action" });

      toast.success(
        `Successfully put ${data.lots.length} ${
          data.lots.length === 1 ? "lot" : "lots"
        } ${statusText}`
      );

      await refreshData();
    } catch (error) {
      console.error(`Error setting ${targetStatus}:`, error);
      toast.error(`Failed to update lots status. Please try again.`);
    }
  };

  const handleRefreshLotDocuments = async (lotId: string) => {
    if (!lotId) return;

    try {
      const res = await store.dispatch("lot/getLot", lotId);
      const updatedLot = res.data;

      const lotIndex = lots.value.findIndex((lot) => lot._id === lotId);

      if (lotIndex !== -1) {
        lots.value[lotIndex].documents = updatedLot.documents;
      }
    } catch (error) {
      toast.error("Failed to refresh documents. Please try again.");
    }
  };

  watch(
    () => estateId.value,
    () => {
      router.push({ name: "StagesV2" });
    }
  );

  onMounted(async () => {
    await updateUserProfile();
    await initializeColumns();

    await initializeData(
      stageId,
      activeFilter.value.value,
      activeFilter.value.label
    );
  });

const __returned__ = { store, toast, stageId, stage, lots, lotList, initialHolds, isLoading, hasInitiallyLoaded, estateId, getStage, getEstate, getLots, getInitialHolds, updateUserProfile, initializeData, isSelectAll, selectedLotsList, selectedForReleaseList, selectedForOptionList, showCreateOption, selectLot, selectAll, clearSelection, extraColumns, customColumns, columnsArray, handleColumnUpdate, initializeColumns, isUpdating, editingLot, editingField, editingValue, fieldsConfig, columnInputTypeMap, getFieldType, getColumnValue, getColumnDisplayValue, isFieldEditable, getFieldOptions, startEditing, saveEdit, cancelEdit, updateEditingValue, filters, activeFilter, viewType, changeFilter, refreshData, showReleaseModal, showRegDateModal, showCreateOptionModal, showTransitionModal, showCustomizeColumnsModal, attentionType, clickAction, closeReleaseModal, closeCreateOptionModal, closeRegDateModal, proceed, onUpdateRegistrationDate, showEditPriceModal, editingLotForPrice, editPriceData, registrationDate, handleFilterChange, handlePriceSave, openEditPriceModal, bulkStatusUpdate, handleCancelHold, handleBulkCancelHold, handleSetHold, handleBulkSetHold, handleBulkSetHoldWithBuyers, handleRefreshLotDocuments, onMounted, watch, computed, ref, get router() { return router }, get formatDate() { return formatDate }, get useStore() { return useStore }, get useToast() { return useToast }, LotsTable, CustomizeColumnsModal, ReleaseLotsModal, CreateOptionModal, SetRegistrationDateModal, TransitionModal, EditPriceModal, get plural() { return plural }, get useLotsData() { return useLotsData }, get useLotsSelection() { return useLotsSelection }, get useLotsEditing() { return useLotsEditing }, get useLotsColumns() { return useLotsColumns }, get useLotsModals() { return useLotsModals }, get useLotsFiltering() { return useLotsFiltering }, get PriceType() { return PriceType }, get LotStatus() { return LotStatus }, get LotConditionsEnum() { return LotConditionsEnum }, get getStatusText() { return getStatusText }, get StatusFiltersLabels() { return StatusFiltersLabels }, get StatusFiltersValues() { return StatusFiltersValues } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})